import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import './SemesterManagementPopover.css';

function SemesterManagementPopover({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    id: null,
    da_semester: '',
    neptun_semester: '',
  });

  const resetForm = () => {
    setForm({
      id: null,
      da_semester: '',
      neptun_semester: '',
    });
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/semesters');
      setItems(response.data || []);
    } catch (err) {
      console.error('Failed to load semester mappings:', err);
      setError(err.response?.data?.detail || 'Félévek betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const payload = {
        id: form.id,
        da_semester: form.da_semester.trim() || null,
        neptun_semester: form.neptun_semester.trim() || null,
      };

      let response;
      if (payload.id) {
        response = await api.put(`/api/admin/semesters/${payload.id}`, payload);
      } else {
        response = await api.post('/api/admin/semesters', payload);
      }

      const saved = response.data;
      setItems((prev) => {
        const others = prev.filter((i) => i.id !== saved.id);
        return [...others, saved].sort((a, b) => a.id - b.id);
      });

      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to save semester mapping:', err);
      setError(err.response?.data?.detail || 'Mentés sikertelen');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      da_semester: item.da_semester || '',
      neptun_semester: item.neptun_semester || '',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a félév-mappolást?')) return;
    try {
      setDeletingId(id);
      await api.delete(`/api/admin/semesters/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error('Failed to delete semester mapping:', err);
      alert(err.response?.data?.detail || 'Törlés sikertelen');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="sem-mgmt-overlay">
      <div className="sem-mgmt-popover">
        <div className="sem-mgmt-header">
          <h2>Félévek</h2>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {error && <div className="sem-mgmt-error">{error}</div>}

        <div className="sem-mgmt-toolbar">
          <button
            className="add-item-button"
            type="button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <Plus size={16} />
            Új félév
          </button>
        </div>

        {showAddForm && (
          <div className="sem-mgmt-add-form-wrapper">
            <form className="sem-mgmt-add-form" onSubmit={handleSave}>
              <div className="form-row">
                <label>DreamApply félév</label>
                <input
                  type="text"
                  name="da_semester"
                  value={form.da_semester}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Neptun félév</label>
                <input
                  type="text"
                  name="neptun_semester"
                  value={form.neptun_semester}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="sem-mgmt-form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Mégse
                </button>
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="sem-mgmt-list">
          {loading ? (
            <div className="sem-mgmt-loading">Betöltés...</div>
          ) : items.length === 0 ? (
            <div className="sem-mgmt-empty">Még nincsenek félév-mappolások.</div>
          ) : (
            items
              .slice()
              .sort((a, b) => a.id - b.id)
              .map((item) => (
                <div key={item.id} className="sem-mgmt-item">
                  <div className="sem-mgmt-content" onDoubleClick={() => handleEdit(item)}>
                    <div className="sem-mgmt-title">
                      <span className="sem-mgmt-da">{item.da_semester || '-'}</span>
                      <span className="sem-mgmt-sep"> - </span>
                      <span className="sem-mgmt-neptun">{item.neptun_semester || '-'}</span>
                    </div>
                    <div className="sem-mgmt-readonly-grid">
                      <div className="sem-mgmt-readonly-field">
                        <label>Neptun félév</label>
                        <input type="text" value={item.neptun_semester || ''} disabled />
                      </div>
                    </div>
                  </div>
                  <div className="sem-mgmt-actions">
                    <button type="button" className="icon-button" onClick={() => handleEdit(item)}>
                      Szerkesztés
                    </button>
                    <button
                      type="button"
                      className="icon-button danger"
                      onClick={() => handleDelete(item.id)}
                      disabled={deletingId === item.id}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default SemesterManagementPopover;
